import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Plus, Shield, RefreshCw, UserX, UserCheck, Mail, Key } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_META = {
  super_admin: { label: 'Super Admin', color: '#7c3aed' },
  admin: { label: 'Admin', color: '#0f172a' },
  manager: { label: 'Manager', color: '#0f172a' },
  sales: { label: 'Sales', color: '#0284c7' },
  operations: { label: 'Operations', color: '#16a34a' },
  content: { label: 'Content', color: '#D4A017' },
  support: { label: 'Support', color: '#0891b2' },
  finance: { label: 'Finance', color: '#dc2626' },
};

export default function EmployeesPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState({});
  const [assignable, setAssignable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newEmp, setNewEmp] = useState({ name: '', email: '', role: 'sales', phone: '', notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [emp, rls] = await Promise.all([
        api('get', '/api/admin/employees'),
        api('get', '/api/admin/rbac/roles'),
      ]);
      setItems(emp.data.items || []);
      setRoles(rls.data.roles || {});
      setAssignable(rls.data.assignable || []);
    } catch (e) { toast.error('Laden mislukt'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createEmployee = async () => {
    if (!newEmp.email || !newEmp.name || !newEmp.role) {
      toast.error('Naam, email en rol zijn vereist');
      return;
    }
    try {
      const r = await api('post', '/api/admin/employees', newEmp);
      toast.success(`${newEmp.name} aangemaakt — uitnodiging: ${r.data.email_status}`);
      setCreating(false);
      setNewEmp({ name: '', email: '', role: 'sales', phone: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aanmaken mislukt'); }
  };

  const updateEmployee = async () => {
    try {
      await api('patch', `/api/admin/employees/${editing.id}`, { name: editing.name, phone: editing.phone, notes: editing.notes, role: editing.role });
      toast.success('Bijgewerkt');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update mislukt'); }
  };

  const disableEmp = async (id) => {
    if (!window.confirm('Medewerker deactiveren?')) return;
    try {
      await api('post', `/api/admin/employees/${id}/disable`);
      toast.info('Gedeactiveerd');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
  };

  const enableEmp = async (id) => {
    await api('post', `/api/admin/employees/${id}/enable`);
    toast.success('Geactiveerd');
    load();
  };

  const resetAccess = async (id) => {
    if (!window.confirm('Toegang opnieuw resetten? Stuurt nieuwe setup-link.')) return;
    try {
      const r = await api('post', `/api/admin/employees/${id}/reset-access`);
      toast.success(`Email status: ${r.data.email_status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
  };

  return (
    <div data-testid="employees-page">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0f172a] flex items-center gap-2"><Users size={20} className="text-[#D4A017]" /> Medewerkers</h1>
          <p className="text-sm text-[#64748b] mt-1">{items.length} accounts · rollen + permissies via RBAC.</p>
        </div>
        <button data-testid="new-employee-btn" onClick={() => setCreating(true)} className="text-xs bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
          <Plus size={12} /> Nieuwe medewerker
        </button>
      </div>

      {/* Roles overview */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
        <p className="text-xs font-bold text-[#64748b] uppercase mb-3 flex items-center gap-1.5"><Shield size={12} /> Beschikbare rollen + permissies</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(roles).filter(([r]) => r !== 'manager').map(([role, perms]) => (
            <div key={role} className="bg-slate-50 rounded-lg p-3">
              <p className="font-bold text-sm" style={{ color: ROLE_META[role]?.color }}>{ROLE_META[role]?.label || role}</p>
              <p className="text-[10px] text-[#94a3b8] mt-1">{perms.length} permissies</p>
            </div>
          ))}
        </div>
      </div>

      {/* Employee list */}
      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? <p className="text-center text-sm text-[#94a3b8] py-10">Laden...</p>
          : items.length === 0 ? <p className="text-center text-sm text-[#94a3b8] py-10">Nog geen medewerkers. Klik "Nieuwe medewerker".</p>
          : (
            <div className="divide-y divide-slate-100">
              {items.map(emp => (
                <div key={emp.id} data-testid={`employee-${emp.id}`} className="p-4 flex justify-between items-center gap-3 flex-wrap hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-sm text-[#0f172a]">{emp.name || emp.email}</p>
                      <span style={{ color: ROLE_META[emp.role]?.color, background: ROLE_META[emp.role]?.color + '20' }} className="text-[10px] font-bold px-2 py-0.5 rounded uppercase">{ROLE_META[emp.role]?.label || emp.role}</span>
                      {emp.disabled && <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Gedeactiveerd</span>}
                      {!emp.password_hash && <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Setup hangt</span>}
                    </div>
                    <p className="text-xs text-[#64748b] mt-1">{emp.email} · {emp.permissions_effective?.length || 0} effectieve permissies</p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => setEditing(emp)} className="bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] text-xs font-semibold p-2 rounded-lg" title="Bewerken"><RefreshCw size={13} /></button>
                    <button onClick={() => resetAccess(emp.id)} data-testid={`reset-${emp.id}`} className="bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] text-xs font-semibold p-2 rounded-lg" title="Reset toegang"><Key size={13} /></button>
                    {emp.disabled
                      ? <button onClick={() => enableEmp(emp.id)} data-testid={`enable-${emp.id}`} className="bg-green-50 hover:bg-green-100 text-green-700 text-xs font-bold p-2 rounded-lg" title="Activeren"><UserCheck size={13} /></button>
                      : <button onClick={() => disableEmp(emp.id)} data-testid={`disable-${emp.id}`} className="bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold p-2 rounded-lg" title="Deactiveren"><UserX size={13} /></button>
                    }
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-md w-full p-5">
            <h2 className="font-bold text-lg text-[#0f172a] mb-4">Nieuwe medewerker</h2>
            <div className="space-y-2">
              {[['name', 'Naam *', 'text'], ['email', 'Email *', 'email'], ['phone', 'Telefoon', 'text']].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-[10px] text-[#64748b] uppercase font-bold tracking-wide">{label}</label>
                  <input data-testid={`new-emp-${k}`} type={type} value={newEmp[k]} onChange={e => setNewEmp({ ...newEmp, [k]: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-[10px] text-[#64748b] uppercase font-bold tracking-wide">Rol *</label>
                <select data-testid="new-emp-role" value={newEmp.role} onChange={e => setNewEmp({ ...newEmp, role: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  {assignable.map(r => <option key={r} value={r}>{ROLE_META[r]?.label || r} ({roles[r]?.length || 0} permissies)</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[#64748b] uppercase font-bold tracking-wide">Notities</label>
                <textarea rows={3} value={newEmp.notes} onChange={e => setNewEmp({ ...newEmp, notes: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <p className="text-[11px] text-[#94a3b8] mt-3"><Mail size={11} className="inline mr-1" /> Setup-link wordt automatisch verstuurd.</p>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setCreating(false)} className="text-sm font-semibold text-[#64748b]">Annuleren</button>
              <button data-testid="save-new-emp-btn" onClick={createEmployee} className="bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] text-sm font-bold px-5 py-2 rounded-lg">Aanmaken</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-md w-full p-5">
            <h2 className="font-bold text-lg text-[#0f172a] mb-4">{editing.name} bewerken</h2>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-[#64748b] uppercase font-bold">Naam</label>
                <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-[#64748b] uppercase font-bold">Rol</label>
                <select value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  {assignable.map(r => <option key={r} value={r}>{ROLE_META[r]?.label || r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[#64748b] uppercase font-bold">Notities</label>
                <textarea rows={3} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setEditing(null)} className="text-sm font-semibold text-[#64748b]">Annuleren</button>
              <button data-testid="save-edit-emp-btn" onClick={updateEmployee} className="bg-[#0f172a] hover:bg-[#1e1b4b] text-white text-sm font-bold px-5 py-2 rounded-lg">Opslaan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
